import React, { useState } from 'react'
import RdnaButton from '@ringdna/common/src/components/RdnaButton'
import styled from 'styled-components'
import RdnaDrawer from '@ringdna/common/src/components/RdnaDrawer'
import RdnaText from '@ringdna/common/src/components/RdnaText'
import { Location, SFUser } from '../../../../store/api/users-api'
import AddNewUsers from './AddNewUsers'
import AssignLicenses from './AssignLicenses'
import SmartNumbersAndLocation from './SmartNumbersAndLocation'
import Success from './Success'
import { RdnaAccount } from '@ringdna/common/src/types/accounts'
import RdnaCheckbox from '@ringdna/common/src/components/RdnaFormElements/RdnaCheckbox'
import RdnaTextInput from '@ringdna/common/src/components/RdnaFormElements/RdnaTextInput'
import RdnaAutocomplete from '@ringdna/common/src/components/RdnaFormElements/RdnaAutoComplete'
import { PubNubProvider } from '../../../contexts/PubNubContext'
import { checkKeyCode } from '../utils'

export type AssignmentUserRoleDrawerProps = {
  isOpen: boolean
  onClose: () => void
  remainingLicenses: { [key: string]: number }
  locations: Location[]
  selectedAccount: RdnaAccount
  onSave: (reload: boolean) => void
}

export default function AddUsers({
  isOpen,
  onClose,
  remainingLicenses,
  locations,
  selectedAccount,
  onSave
}: AssignmentUserRoleDrawerProps) {
  const [disableDone, setDisableDone] = useState(true)
  const [page, setPage] = useState(1)
  const [userList, setUserList] = useState<SFUser[]>([])
  const [areaCode, setAreaCode] = useState<string>('')
  const [assignAreaCode, setAssignAreaCode] = useState<boolean>(false)
  const [extension, setExtension] = useState<string>('')
  const [assignExtension, setAssignExtension] = useState<boolean>(false)
  const [location, setLocation] = useState<Location>({ id: -1, name: '' })
  const [assignLocation, setAssignLocation] = useState<boolean>(false)

  const heading = () => {
    if (page == 1) {
      return 'Add New Users'
    } else if (page == 2) {
      return 'Assign Licenses'
    } else if (page == 3) {
      return 'Smart Numbers & Location'
    } else {
      return 'Success'
    }
  }

  const subHeading = () => {
    if (page == 1) {
      return 'Add new users to Revenue.io.'
    } else if (page == 2) {
      return 'Assign licenses to new users. The number of available licenses are noted below.'
    } else if (page == 3) {
      return 'Assign Smart Numbers to new users and their Location.'
    } else {
      return "You've successfully added the following users:"
    }
  }

  const closeDrawer = () => {
    onClose()
    page == 4 && onSave(true)
    setPage(1)
  }

  function handleDone() {
    closeDrawer()
    onSave(true)
  }

  function handleNext() {
    const goToSmartNumbers = userList.find(user => user.dialerLicense)
    setPage(goToSmartNumbers ? 3 : 4)
  }

  function handleAdd() {
    setPage(4)
  }

  return (
    <Drawer
      open={isOpen}
      heading={heading()}
      subHeading={subHeading()}
      onClose={closeDrawer}
      onBack={page > 1 && page !== 4 ? () => setPage(page - 1) : undefined}
    >
      {page == 1 && <AddNewUsers userList={userList} setUserList={setUserList} />}
      {page == 2 && (
        <AssignLicenses
          userList={userList.sort((a, b) => (a.name < b.name ? -1 : 1))}
          setUserList={setUserList}
          remainingLicenses={remainingLicenses}
        />
      )}
      {page == 3 && (
        <Container>
          <RdnaText bold>{'Smart Numbers'}</RdnaText>
          <Row>
            <RdnaCheckbox
              className={'sn-checkbox'}
              onChange={checked => setAssignAreaCode(checked)}
              value={assignAreaCode}
              disabled={!areaCode}
              label={'Assign Smart Numbers to all new users using the area code:'}
            />
            <RdnaTextInput
              className={'sn-text'}
              placeholder={'Area Code'}
              onKeyDown={checkKeyCode}
              onChange={({ target: { value } }) => {
                !value && setAssignAreaCode(false)
                setAreaCode(value)
              }}
              type="number"
              value={areaCode}
            />
          </Row>
          <Row>
            <RdnaCheckbox
              className={'sn-checkbox'}
              onChange={checked => setAssignExtension(checked)}
              value={assignExtension}
              disabled={!extension}
              label={'Assign Extension to all new users starting from this number:'}
            />
            <RdnaTextInput
              className={'sn-text'}
              placeholder={'Extension'}
              onKeyDown={checkKeyCode}
              onChange={({ target: { value } }) => {
                !value && setAssignExtension(false)
                setExtension(value)
              }}
              type="number"
              value={extension}
            />
          </Row>
          <RdnaText bold>{'Location'}</RdnaText>
          <Row>
            <RdnaCheckbox
              className={'sn-checkbox'}
              onChange={checked => setAssignLocation(checked)}
              value={assignLocation}
              disabled={!location || location?.id == -1}
              label={'Assign all users this location:'}
            />
            <RdnaAutocomplete
              id={`user-location`}
              className={'sn-cell-select'}
              onChange={(event, value) => {
                setLocation(value)
                !value && setAssignLocation(false)
              }}
              options={locations}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              getOptionLabel={option => option.name}
              placeholder={'Location'}
              value={location}
            />
          </Row>
          <SmartNumbersAndLocation
            userList={userList}
            setUserList={setUserList}
            locations={locations}
            assignAreaCode={assignAreaCode}
            assignExtension={assignExtension}
            assignLocation={assignLocation}
          />
        </Container>
      )}
      {page == 4 && (
        <PubNubProvider>
          <Success
            userList={userList}
            accountId={selectedAccount.id}
            areaCode={assignAreaCode && areaCode.length > 0 ? areaCode : null}
            extension={assignExtension && extension.length > 0 ? extension : null}
            locationId={assignLocation ? location.id : null}
            setDisableDone={setDisableDone}
            closeDrawer={closeDrawer}
          ></Success>
        </PubNubProvider>
      )}
      <Footer>
        {page != 4 && (
          <RdnaButton
            text={'Cancel'}
            color="neutral"
            variant="text"
            onClick={page > 1 ? () => setPage(page - 1) : closeDrawer}
          />
        )}
        {page == 1 && <RdnaButton text={'Next'} onClick={() => setPage(2)} disabled={!userList.length}></RdnaButton>}
        {page == 2 && (
          <RdnaButton
            text={'Next'}
            onClick={handleNext}
            disabled={
              !(
                userList.find(user => user.adminLicense) ||
                userList.find(user => user.dialerLicense) ||
                userList.find(user => user.calendarLicense) ||
                userList.find(user => user.gsLicense) ||
                userList.find(user => user.caiLicense) ||
                userList.find(user => user.momentsLicense) ||
                userList.find(user => user.revenueAILicense)
              )
            }
          ></RdnaButton>
        )}
        {page == 3 && (
          <RdnaButton
            text={'Add Users'}
            onClick={handleAdd}
            disabled={selectedAccount.settings.locationRequired && !assignLocation && userList.some(u => !u.location)}
          ></RdnaButton>
        )}
        {page == 4 && <RdnaButton text={'Done'} onClick={handleDone} disabled={disableDone}></RdnaButton>}
      </Footer>
    </Drawer>
  )
}

const Drawer = styled(RdnaDrawer)`
  > div {
    width: 70%;
  }
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  .break-column {
    margin-right: ${({ theme }) => 2 * theme.spacing}px;
    word-break: break-word;
    white-space: normal;
    width: auto;
    height: auto;
  }
`

const Footer = styled.div`
  display: flex;
  justify-content: end;
  padding-top: ${props => props.theme.spacing * 5}px;
`

const Container = styled.div`
  .sn-checkbox {
    width: auto;
    label > span:nth-child(2) {
      color: ${props => props.theme.palette.text.primary} !important;
    }
  }
  .sn-text {
    width: 100px;
  }
  [role='cell']:has(.sn-cell-text, .sn-cell-select) {
    overflow: visible;
  }
  .sn-cell-text {
    width: 100px;
    display: inline-table;
    margin: 0;
  }
  .sn-cell-select {
    width: 200px;
    display: inline-table;
    margin-top: ${props => props.theme.spacing * 0.5}px;
  }
`

const Row = styled.div`
  width: 100%;
  display: flex;
`
